import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import MultiSearch from "../../../Components/SearchInputs/MultiSearch/MultiSearch";
import { apiService } from "../../../api/api";
import { MusicAuditStats } from "../../../utils/@types";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import { buildQueryString, parseStringParam } from "../../../utils/urlFilters";
import { trackToolAction } from "../../../utils/analytics";

type StatsFilters = {
  legalEntityType: string;
  channelId: string;
  channelManagerName: string;
};

const DEFAULT_FILTERS: StatsFilters = {
  legalEntityType: "",
  channelId: "",
  channelManagerName: "",
};

const buildStatsParams = (auditType: string, filters: StatsFilters) => ({
  auditType,
  legalEntityType: filters.legalEntityType || "ALL",
  channelId: filters.channelId || "ALL",
  channelManagerName: filters.channelManagerName || "ALL",
});

const formatNumber = (value?: number) =>
  (value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const MusicAssetAuditsDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      legalEntityType: parseStringParam(params, "legalEntityType"),
      channelId: parseStringParam(params, "channelId"),
      channelManagerName: parseStringParam(params, "channelManagerName"),
    };
  }, [location.search]);

  const [pendingFilters, setPendingFilters] =
    useState<StatsFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<StatsFilters>(initialFilters);

  useEffect(() => {
    setPendingFilters(initialFilters);
    setAppliedFilters(initialFilters);
  }, [initialFilters]);

  const [incorrectStats, setIncorrectStats] = useState<MusicAuditStats | null>(
    null
  );
  const [mismatchedStats, setMismatchedStats] =
    useState<MusicAuditStats | null>(null);
  const [stubStats, setStubStats] = useState<MusicAuditStats | null>(null);
  const [missingStats, setMissingStats] = useState<MusicAuditStats | null>(null);

  const handleSearch = useCallback(() => {
    trackToolAction("DataHealthTool", "search");
    setAppliedFilters(pendingFilters);
    const query = buildQueryString({
      legalEntityType: pendingFilters.legalEntityType,
      channelId: pendingFilters.channelId,
      channelManagerName: pendingFilters.channelManagerName,
    });
    navigate({ search: query }, { replace: true });
  }, [navigate, pendingFilters]);

  const handleReset = useCallback(() => {
    setPendingFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    navigate({ search: "" }, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      const [incorrect, mismatched, stub, missing] = await Promise.all([
        apiService.musicAssetAudit.getMusicAuditStats(
          buildStatsParams("incorrectAssetTypes", appliedFilters)
        ),
        apiService.musicAssetAudit.getMusicAuditStats(
          buildStatsParams("mismatchedSoundRecordings", appliedFilters)
        ),
        apiService.musicAssetAudit.getMusicAuditStats(
          buildStatsParams("stubAssets", appliedFilters)
        ),
        apiService.musicAssetAudit.getMusicAuditStats(
          buildStatsParams("missingSoundRecordings", appliedFilters)
        ),
      ]);

      setIncorrectStats(incorrect);
      setMismatchedStats(mismatched);
      setStubStats(stub);
      setMissingStats(missing);
    };

    fetchStats().catch((error) => {
      console.error("Error fetching music audit stats:", error);
    });
  }, [appliedFilters]);

  const summaryCards = useMemo(
    () => [
      {
        title: "Incorrect Asset Types",
        totalLabel: "Total Incorrect Asset Types",
        stats: incorrectStats,
        uniqueValue: incorrectStats?.uniqueAssetIds,
        uniqueLabel: "Unique Asset IDs",
      },
      {
        title: "Mismatched Sound Recordings",
        totalLabel: "Total Mismatches",
        stats: mismatchedStats,
        uniqueValue: mismatchedStats?.uniqueAssetCodes,
        uniqueLabel: "Unique Asset Codes",
      },
      {
        title: "Stub Assets",
        totalLabel: "Total Issues",
        stats: stubStats,
        uniqueValue: stubStats?.uniqueCustomIds,
        uniqueLabel: "Unique Custom IDs",
      },
      {
        title: "Missing Sound Recordings",
        totalLabel: "Total Issues",
        stats: missingStats,
        uniqueValue: missingStats?.uniqueAssetCodes,
        uniqueLabel: "Unique Asset Codes",
      },
    ],
    [incorrectStats, mismatchedStats, stubStats, missingStats]
  );

  return (
    <div style={{ width: "100%", marginTop: "-30px", marginBottom: "-30px" }}>
      <Card
        className="flex-column md:flex-col gap-3"
        style={{
          marginTop: "0.3rem",
          marginBottom: "2rem",
          boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <div className="grid-container" style={{ marginTop: "-1rem" }}>
          <div className="filter-item">
            <MultiSearch
              selectedItems={
                pendingFilters.legalEntityType
                  ? [pendingFilters.legalEntityType]
                  : []
              }
              value={(e) => {
                const newArray = typeof e === "function" ? e([]) : e;
                setPendingFilters((prev) => ({
                  ...prev,
                  legalEntityType: newArray[0] || "",
                }));
              }}
              label={"Legal Entity Type"}
              parseType="both"
            />
          </div>
          <div className="filter-item">
            <MultiSearch
              selectedItems={
                pendingFilters.channelId ? [pendingFilters.channelId] : []
              }
              value={(e) => {
                const newArray = typeof e === "function" ? e([]) : e;
                setPendingFilters((prev) => ({
                  ...prev,
                  channelId: newArray[0] || "",
                }));
              }}
              label={"Channel ID"}
              parseType="both"
            />
          </div>
          <div className="filter-item">
            <MultiSearch
              selectedItems={
                pendingFilters.channelManagerName
                  ? [pendingFilters.channelManagerName]
                  : []
              }
              value={(e) => {
                const newArray = typeof e === "function" ? e([]) : e;
                setPendingFilters((prev) => ({
                  ...prev,
                  channelManagerName: newArray[0] || "",
                }));
              }}
              label={"Channel Manager"}
              parseType="both"
            />
          </div>
        </div>
        <div className="grid-container">
          <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
            <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
              <Button label="Search" icon="pi pi-search" onClick={handleSearch} />
            </div>
            <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
              <Button
                label="Reset"
                icon="pi pi-replay"
                severity="danger"
                onClick={handleReset}
              />
            </div>
          </div>
        </div>
      </Card>

      {summaryCards.map((card) => (
        <div key={card.title}>
          <h3 style={{ marginTop: "1.5rem" }}>{card.title}</h3>
          <Card
            className="mb-3"
            style={{
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "7px",
            }}
          >
            <div className="grid">
              <div className="col-12 md:col-3">
                <div className="text-center p-3">
                  <div className="text-2xl font-bold text-blue-600">
                    {formatNumber(card.stats?.totalIssues)}
                  </div>
                  <div className="text-sm text-gray-600">{card.totalLabel}</div>
                </div>
              </div>
              <div className="col-12 md:col-3">
                <div className="text-center p-3">
                  <div className="text-2xl font-bold text-green-600">
                    {formatNumber(card.uniqueValue)}
                  </div>
                  <div className="text-sm text-gray-600">{card.uniqueLabel}</div>
                </div>
              </div>
              <div className="col-12 md:col-3">
                <div className="text-center p-3">
                  <div className="text-2xl font-bold text-orange-600">
                    {formatNumber(card.stats?.newIssues)}
                  </div>
                  <div className="text-sm text-gray-600">
                    New Issues (7 days)
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-3">
                <div className="text-center p-3">
                  <div className="text-2xl font-bold text-purple-600">
                    {formatNumber(card.stats?.totalApproxDailyViews)}
                  </div>
                  <div className="text-sm text-gray-600">Total Daily Views</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      ))}
    </div>
  );
};

export default MusicAssetAuditsDashboard;
